import React, { useEffect, useState } from "react";
import { registerClient } from "../../services/admin/api";
import "./registration.css";
import { useNavigate } from "react-router-dom";
import { FiUser, FiMail, FiLock, FiPhone, FiBriefcase, FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "react-toastify";

const Registration = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    companyName: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [strength, setStrength] = useState("");

  const [show, setShow] = useState({
    password: false,
    confirmPassword: false
  });

  useEffect(() => {
    document.body.classList.add("registration-page");
    return () => document.body.classList.remove("registration-page");
  }, []);

  // -------- PASSWORD STRENGTH --------
  const checkStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return "weak";
    if (score <= 4) return "medium";
    return "strong";
  };

  const validateRules = (pwd) => {
    const missing = [];
    if (!/[A-Z]/.test(pwd)) missing.push("uppercase");
    if (!/[a-z]/.test(pwd)) missing.push("lowercase");
    if (!/[0-9]/.test(pwd)) missing.push("number");
    if (!/[^A-Za-z0-9]/.test(pwd)) missing.push("special character");
    if (pwd.length < 8) missing.push("8 characters");
    return missing;
  };

  // -------- BLOCK COPY/PASTE/CUT/DRAG FOR PASSWORD --------
  const blockCopyPaste = (e) => {
    e.preventDefault();
    toast.warn("Copy-paste is not allowed for passwords");
  };

  // -------- FILTERED INPUT HANDLER --------
  const handleFilteredChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const onlyDigits = value.replace(/\D/g, "");
      setForm({ ...form, phoneNumber: onlyDigits.slice(0, 10) });
      setFieldErrors({ ...fieldErrors, phoneNumber: "" });
      return;
    }

    if (name === "username" || name === "companyName") {
      let cleaned = value.replace(/[^A-Za-z0-9 ]/g, "");
      cleaned = cleaned.replace(/\s+/g, " ").trimStart();
      const isNumericOnly = /^[0-9 ]*$/.test(cleaned);

      setForm({ ...form, [name]: cleaned });

      if (isNumericOnly && cleaned.trim() !== "") {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Must contain at least one alphabet"
        });
      } else {
        setFieldErrors({ ...fieldErrors, [name]: "" });
      }
      return;
    }

    const updatedForm = { ...form, [name]: value };
    setForm(updatedForm);

    let updatedErrors = { ...fieldErrors, [name]: "" };

    if (name === "password" || name === "confirmPassword") {
      if (updatedForm.password === updatedForm.confirmPassword) {
        updatedErrors.confirmPassword = "";
      }
    }

    setFieldErrors(updatedErrors);

    if (name === "password") {
      setStrength(checkStrength(value));
    }
  };

  // -------- SUBMIT --------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setFieldErrors({});

    const errors = {};

    Object.keys(form).forEach((key) => {
      if (!form[key].trim()) {
        errors[key] = "This field is required";
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!/^[0-9]{10}$/.test(form.phoneNumber)) {
      setFieldErrors({ phoneNumber: "Phone must be 10 digits" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setFieldErrors({ email: "Invalid email format" });
      return;
    }

    const ruleErrors = validateRules(form.password);
    if (ruleErrors.length > 0) {
      setFieldErrors({
        password: `Must include: ${ruleErrors.join(", ")}`
      });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    setLoading(true);

    try {
      const res = await registerClient(form);
      if (res?.status === true) {
        toast.success(
          res?.message ||
          "Registration successful. Waiting for admin approval."
        );

        navigate("/");
      } else {
        toast.error(
          res?.message ||
          "Registration rejected."
        );
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Something went wrong";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // -------- DISABLE BUTTON IF ANY ERROR OR EMPTY FIELD --------
  const hasFieldErrors = Object.values(fieldErrors).some((err) => typeof err === "string" && err.trim() !== "");
  const hasEmptyFields = Object.values(form).some(v => v.trim() === "");
  const isSubmitDisabled = loading || hasFieldErrors || hasEmptyFields;

  return (
    <div className="registration-wrapper">
      <div className="registration-form-container">
        <h2 className="registration-title">Client Registration</h2>
        <p className="registration-subtitle">Request access to the BGV platform</p>

        <form onSubmit={handleSubmit}>

          {/* Username */}
          <div className="registration-input-group">
            <label>Username *</label>
            <div className="registration-input-wrapper">
              <FiUser className="registration-input-icon" />
              <input
                name="username"
                value={form.username}
                onChange={handleFilteredChange}
              />
            </div>
            {fieldErrors.username && <small className="error-text">{fieldErrors.username}</small>}
          </div>

          {/* Company */}
          <div className="registration-input-group">
            <label>Company Name *</label>
            <div className="registration-input-wrapper">
              <FiBriefcase className="registration-input-icon" />
              <input
                name="companyName"
                value={form.companyName}
                onChange={handleFilteredChange}
              />
            </div>
            {fieldErrors.companyName && <small className="error-text">{fieldErrors.companyName}</small>}
          </div>

          {/* Phone */}
          <div className="registration-input-group">
            <label>Phone Number *</label>
            <div className="registration-input-wrapper">
              <FiPhone className="registration-input-icon" />
              <input
                type="tel"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleFilteredChange}
              />
            </div>
            {fieldErrors.phoneNumber && <small className="error-text">{fieldErrors.phoneNumber}</small>}
          </div>

          {/* Email */}
          <div className="registration-input-group">
            <label>Email *</label>
            <div className="registration-input-wrapper">
              <FiMail className="registration-input-icon" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleFilteredChange}
              />
            </div>
            {fieldErrors.email && <small className="error-text">{fieldErrors.email}</small>}
          </div>

          {/* Password */}
          <div className="registration-input-group">
            <label>Password *</label>
            <div className="registration-input-wrapper">
              <FiLock className="registration-input-icon" />
              <input
                type={show.password ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleFilteredChange}
                onPaste={blockCopyPaste}
                onCopy={blockCopyPaste}
                onCut={blockCopyPaste}
                onContextMenu={blockCopyPaste}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />
              <span
                className="eye"
                onClick={() => setShow({ ...show, password: !show.password })}
              >
                {show.password ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>

            {form.password && (
              <>
                <div className={`strength-bar ${strength}`} />
                {validateRules(form.password).length > 0 && (
                  <small className="password-hint" style={{ color: 'red' }}>
                    Must contain uppercase, lowercase, number, special character, min 8 chars
                  </small>
                )}
              </>
            )}

            {fieldErrors.password && <small className="error-text">{fieldErrors.password}</small>}
          </div>

          {/* Confirm Password */}
          <div className="registration-input-group">
            <label>Confirm Password *</label>
            <div className="registration-input-wrapper">
              <FiLock className="registration-input-icon" />
              <input
                type={show.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={handleFilteredChange}
                onPaste={blockCopyPaste}
                onCopy={blockCopyPaste}
                onCut={blockCopyPaste}
                onContextMenu={blockCopyPaste}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />
              <span
                className="eye"
                onClick={() =>
                  setShow({ ...show, confirmPassword: !show.confirmPassword })
                }
              >
                {show.confirmPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>

            {fieldErrors.confirmPassword && (
              <small className="error-text">{fieldErrors.confirmPassword}</small>
            )}
          </div>

          <button
            className={`registration-button ${isSubmitDisabled ? "btn-disabled" : ""}`}
            disabled={isSubmitDisabled}
          >
            {loading ? "Submitting..." : "Register"}
          </button>
        </form>

        <div className="registration-loginLink">
          <span>Already have an account? </span>
          <a onClick={() => navigate("/")}>Back to Login</a>
        </div>
      </div>
    </div>
  );
};

export default Registration;
